import React, { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { Grid, notification, Collapse } from "antd";
import moment from "moment";
import { RcFile } from "antd/es/upload";
import { UploadFile } from "antd/es/upload/interface";

import { Button, Select, Input, ImageUploader, TextArea } from "components";
import {
  ConfirmFailIcon,
  ConfirmDeliveryIcon,
  SendCompleteIcon,
  CloseIcon,
} from "icons";
import {
  handleGetTimeByTimeZone,
  localStorageHelper,
  handleTimeOptions,
} from "utils";

import {
  GetMessageHistoryListFilterActionCreators,
  UpdateMessageActionCreators,
  DeleteMessageActionCreators,
} from "redux/rootActions";
import {
  selectIsLoading as selectIsLoadingDeleteMessage,
  deleteMessageDataResponse,
} from "redux/messagemanagement/deleteMessage/deleteMessageStates";
import {
  selectIsLoading,
  updateMessageDataResponse,
} from "redux/messagemanagement/updateMessage/updateMessageStates";
import { httpStatus } from "configs/httpStatus";

import {
  Text,
  ConfirmContainer,
  TextConfirm2,
  ButtonContainer,
  ConfirmModalStyled,
  ConfirmBtnStyled,
  FailText,
  SendMessageModalStyled,
  Flex,
  DeleteMessage,
  WrapperDateAndTime,
  WrapperDateAndTimeContainer,
  WrapperDateAndTimeContent,
  ButtonStyled,
  SectionContainer,
  Label,
  TagsContainer,
  BadgeContainer,
  CollapseStyled2,
} from "./MessageDelivery.style";
import { DatePicker2 } from "./components";
import { format, timeValueDefaults } from "./constants";

const { useBreakpoint } = Grid;
const { Panel } = Collapse;

interface UploadFileType {
  uid: string;
  name: string;
  status: "done" | "uploading";
  url?: string;
  originFileObj?: File; // Only for locally uploaded files
}

interface ISendMessageModal {
  isVisible: boolean;
  handleCancel: () => void;
  individualInfor: any;
  params: any;
  messageID: number;
  setIndividualInfor: (info: any) => void;
  setActiveKey: (key: any) => void;
}

const schema = yup.object().shape({
  message_title: yup.string().trim().required("message-title-required"),
  message: yup.string().required("message-required"),
});

interface ApiFile {
  file_name: string;
  file_path: string; // Cloud storage URL
  id: number;
}

const cleanFilePath = (filePath: string) => {
  const index = filePath.indexOf("?X-Amz-Algorithm=");
  return index !== -1 ? filePath.substring(0, index) : filePath;
};

const fetchApiFile = async (
  file: ApiFile,
  index: number
): Promise<UploadFile<any>> => {
  try {
    const response = await fetch(cleanFilePath(file.file_path));
    const blob = await response.blob();

    const convertedFile = new File([blob], file.file_name, {
      type: blob.type,
      lastModified: new Date().getTime(),
    });

    return {
      uid: `${file.id}`,
      name: file.file_name,
      fileName: file.file_name,
      status: "done",
      url: cleanFilePath(file.file_path),
      originFileObj: convertedFile as RcFile,
      size: blob.size,
      type: blob.type,
      lastModified: convertedFile.lastModified,
      lastModifiedDate: new Date(convertedFile.lastModified),
    };
  } catch (error) {
    return {
      uid: `api-${index}`,
      name: file.file_name,
      status: "error",
      url: cleanFilePath(file.file_path),
    };
  }
};

const useApiFiles = (apiFiles: ApiFile[]) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  useEffect(() => {
    if (apiFiles?.length) {
      const loadFiles = async () => {
        const convertedFiles = await Promise.all(apiFiles.map(fetchApiFile));
        setFileList(convertedFiles);
      };

      loadFiles();
    }
  }, [apiFiles]);

  return fileList;
};

const UpdateMessage: React.FC<ISendMessageModal> = ({
  isVisible,
  handleCancel,
  individualInfor,
  params,
  messageID,
  setIndividualInfor,
  setActiveKey,
}) => {
  const { t }: any = useTranslation("translation", {
    keyPrefix: "message-delivery",
  });
  const { t: t1 }: any = useTranslation("translation", {
    keyPrefix: "user-list",
  });
  const { t: t2 }: any = useTranslation("translation", {
    keyPrefix: "mental-health",
  });
  const { handleSubmit, control, watch, reset, clearErrors, setValue } =
    useForm({
      resolver: yupResolver(schema),
    });
  const screens = useBreakpoint();

  const currentLanguage = localStorageHelper.getItem("i18nextLng");
  const currentTimeZone = localStorageHelper.getItem("currentTimeZone");

  const dispatch = useDispatch();
  const updateMessageLoading = useSelector(selectIsLoading);
  const deleteMessageLoading = useSelector(selectIsLoadingDeleteMessage);

  const deleteMessageData = useSelector(deleteMessageDataResponse);
  const updateMessageData = useSelector(updateMessageDataResponse);

  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [isVisibleNotification, setIsVisibleNotification] = useState({
    type: "",
    status: false,
  });
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [timeOptions, setTimeOptions] = useState<any>([]);
  const [timeValue, setTimeValue] = useState<string>();
  const [isShowDeleteModal, setIsShowDeleteModal] = useState(false);
  const [fileList, setFileList] = useState<any>([]);

  const filesList = useApiFiles(individualInfor?.message_documents);
  const messageTitle = watch("message_title");
  const messageContent = watch("message");

  useEffect(() => {
    dispatch(DeleteMessageActionCreators.handleResetAction());
    dispatch(UpdateMessageActionCreators.handleResetAction());
  }, []);

  const handleGetHour = (date: any) => {
    return moment(
      handleGetTimeByTimeZone("YYYY/MM/DD HH:mm", currentTimeZone, date)
    ).hour();
  };

  const handleTime = () => {
    const hour: any = handleGetHour(new Date()) + 1;
    const timeOptions = handleTimeOptions(hour);

    return {
      timeOptions,
      hour,
    };
  };

  useEffect(() => {
    if (Object.keys(individualInfor).length) {
      if (individualInfor.startDate) {
        const startDateConverted = handleGetTimeByTimeZone(
          format,
          currentTimeZone,
          individualInfor.startDate
        );
        const currentDate = handleGetTimeByTimeZone(
          format,
          currentTimeZone,
          new Date()
        );

        const hour = handleGetHour(individualInfor.startDate);

        if (currentDate === startDateConverted) {
          const hour =
            moment(
              handleGetTimeByTimeZone(
                "YYYY/MM/DD HH:mm",
                currentTimeZone,
                new Date()
              )
            ).hour() + 1;
          setTimeOptions(handleTimeOptions(hour));
        } else {
          setTimeOptions(timeValueDefaults);
        }

        setStartDate(new Date(startDateConverted));
        setTimeValue(`${hour < 10 ? `0${hour}` : hour}:00`);
      }
      setValue("message_title", individualInfor.subject);
      setValue("message", individualInfor.content);
      if (individualInfor.message_documents?.length) {
        if (filesList?.length) {
          const files = filesList.map((file: any) => {
            return {
              file: file,
              fileList: filesList,
            };
          });
          setFileList(files);
        }
      }
    }
  }, [individualInfor, filesList]);

  const onUpdateMessageAPI = () => {
    const formData = new FormData();
    const userIDs = individualInfor.address.map((ad: any) => ad.id);

    formData.append("title", messageTitle);
    formData.append("description", messageContent);
    formData.append("patient_id", userIDs.join(",") || "");
    formData.append("is_send_all", false as any);

    const start = moment(startDate).format(format) + " " + timeValue;
    formData.append("is_send_immediately", false as any);
    formData.append("start_time", start);

    if (fileList?.length) {
      fileList.map((item: any) => {
        if (!item.isDelete) {
          formData.append(
            `message_documents[][document]`,
            item.file.originFileObj
          );
        }
      });
    }
    if (
      !fileList?.length ||
      (fileList?.length && fileList.every((file: any) => file.isDelete))
    ) {
      formData.append(`message_documents[][document]`, "");
    }

    dispatch(
      UpdateMessageActionCreators.updateMessageAction({
        id: messageID,
        param: formData,
      })
    );
  };

  const handleCancelSendMessageModal = () => {
    dispatch(UpdateMessageActionCreators.handleResetAction());
    handleCancel();
    dispatch(
      GetMessageHistoryListFilterActionCreators.messageHistoryListAction({
        ...params,
        page: 1,
      })
    );
  };

  const handleConfirmMessage = () => {
    setIsVisibleNotification({
      ...isVisibleNotification,
      type: "success",
      status: true,
    });
  };

  const handleFailMessage = () => {
    setIsVisibleNotification({
      ...isVisibleNotification,
      type: "fail",
      status: true,
    });
  };

  const handleCancelNotication = () => {
    setIsVisibleNotification({ type: "", status: false });
    handleCancelSendMessageModal();
    setActiveKey(undefined);
  };

  const handleCancelConfirmMessage = () => {
    setShowConfirm(false);
  };

  useEffect(() => {
    if (updateMessageData) {
      if (updateMessageData.status === httpStatus.StatusOK) {
        handleConfirmMessage();
      } else {
        handleFailMessage();
      }
    }
  }, [updateMessageData]);

  useEffect(() => {
    if (deleteMessageData && deleteMessageData.status === httpStatus.StatusOK) {
      setIsShowDeleteModal(false);
      handleCancel();
      dispatch(DeleteMessageActionCreators.handleResetAction());
      dispatch(
        GetMessageHistoryListFilterActionCreators.messageHistoryListAction({
          ...params,
          page: 1,
        })
      );
      notification.success({
        message: t2("deleted"),
        description: "",
        icon: <ConfirmFailIcon width={53} height={53} />,
        placement: "bottomRight",
      });
    }
  }, [deleteMessageData]);

  const handleDeleteMessage = () => {
    dispatch(
      DeleteMessageActionCreators.deleteMessageAction({
        id: messageID,
      })
    );
  };

  const onUploadFileChange = (someArg: any) => {
    setFileList(someArg);
  };

  const SuccessModal = () => (
    <ConfirmModalStyled
      type="form"
      title=""
      visible={isVisibleNotification.status}
      onCancel={handleCancelNotication}
    >
      <div style={{ textAlign: "center", marginTop: 24 }}>
        <ConfirmDeliveryIcon
          style={{ filter: "drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25))" }}
        />

        <Text
          size={36}
          lnHeight="36px"
          weight={700}
          color="#2ac769"
          mg="12px 0px 24px"
        >
          {t("delivery-reservation-completed")}
        </Text>
        <Text size={16} lnHeight="24px" weight={400}>
          {t("delivery-reservation-has-completed")}
        </Text>
        <Text size={16} lnHeight="24px" weight={400} mg="0px 0px 24px">
          {t("close-window")}
        </Text>
        <ConfirmBtnStyled
          background="#E0E0E0"
          color="#FFFFFF"
          padding="11px 96px 13px"
          name={t("close-up")}
          border="none"
          bdr="6px"
          fontSize={16}
          lineHeight="16px"
          onClick={handleCancelNotication}
        />
      </div>
    </ConfirmModalStyled>
  );

  const FailureModal = () => (
    <ConfirmModalStyled
      type="form"
      title=""
      visible={isVisibleNotification.status}
      onCancel={handleCancelNotication}
    >
      <div style={{ textAlign: "center", marginTop: 24 }}>
        <ConfirmFailIcon />
        <Text
          size={36}
          lnHeight="36px"
          weight={700}
          color="#fb4e4e"
          mg="20px 0px 24px"
        >
          {t("transmission-failure")}
        </Text>
        <FailText size={16} lnHeight="24px" weight={400}>
          {t("failed-to-send-the-message")}
        </FailText>
        <FailText size={16} lnHeight="24px" weight={400} mg="0px 0px 24px">
          {t("close-window-contact-support")}
        </FailText>
        <ConfirmBtnStyled
          background="#E0E0E0"
          color="#FFFFFF"
          padding="11px 96px 13px"
          name={t("close-up")}
          border="none"
          bdr="6px"
          fontSize={16}
          lineHeight="16px"
          onClick={handleCancelNotication}
        />
      </div>
    </ConfirmModalStyled>
  );

  const renderNotificationModal = () => {
    if (isVisibleNotification.type === "success") {
      return <SuccessModal />;
    }
    return <FailureModal />;
  };

  const ConfirmModal = () => {
    return (
      <ConfirmContainer>
        <TextConfirm2>
          <div className="text-color">
            {moment(startDate).format("YYYY/MM/DD")}
            {currentLanguage !== "ja" && (
              <span style={{ marginLeft: 5, color: "#333333" }}>at</span>
            )}
            <span style={{ marginLeft: 5 }}>{timeValue}</span>
            {currentLanguage === "ja" && (
              <span style={{ color: "#333333" }}>で</span>
            )}
          </div>
          <div>{t("schedule-message")}</div>
          <div>{t("are-you-ok")}</div>
        </TextConfirm2>
        <ButtonContainer>
          <Button
            background="#E0E0E0"
            color="#FFFFFF"
            padding="12px 24px"
            name={t1("cancel")}
            border="none"
            bdr="6px"
            fontSize={16}
            fontWeight={700}
            lineHeight="16px"
            onClick={handleCancelConfirmMessage}
          />
          <Button
            background="#2AC769"
            color="#FFFFFF"
            padding="12px 48px"
            name={t("yes")}
            border="none"
            bdr="6px"
            fontSize={16}
            fontWeight={700}
            lineHeight="16px"
            disabled={updateMessageLoading}
            onClick={onUpdateMessageAPI}
            style={{
              minWidth: 128,
              height: 40,
              padding: updateMessageLoading ? "10px 48px 14px" : "12px 48px",
            }}
            loading={updateMessageLoading}
          />
        </ButtonContainer>
      </ConfirmContainer>
    );
  };

  const DeleteMessageModal = () => {
    return (
      <ConfirmContainer>
        <TextConfirm2>
          <div>{t("delete-message-noti")}</div>
          <div>{t("are-you-ok")}</div>
        </TextConfirm2>
        <ButtonContainer>
          <Button
            background="#E0E0E0"
            color="#FFFFFF"
            padding="12px 24px"
            name={t1("cancel")}
            border="none"
            bdr="6px"
            fontSize={16}
            fontWeight={700}
            lineHeight="16px"
            onClick={() => setIsShowDeleteModal(false)}
          />
          <Button
            background="#FB2121"
            color="#FFFFFF"
            padding="12px 48px"
            name={t("yes")}
            border="none"
            bdr="6px"
            fontSize={16}
            fontWeight={700}
            lineHeight="16px"
            disabled={deleteMessageLoading}
            onClick={handleDeleteMessage}
            style={{
              minWidth: 128,
              height: 40,
              padding: deleteMessageLoading ? "10px 48px 14px" : "12px 48px",
            }}
            loading={deleteMessageLoading}
          />
        </ButtonContainer>
      </ConfirmContainer>
    );
  };

  const onChangeTime = (value: any) => {
    setTimeValue(value);
  };

  const renderMessageInfo = () => (
    <>
      <form onSubmit={handleSubmit(() => setShowConfirm(true))}>
        <SectionContainer style={{ marginTop: 8 }}>
          <Label>{t("address")}</Label>
          <TagsContainer>
            <BadgeContainer>
              {/* {isSendAll ? (
                  <SendAllBadge>{t("all-registrants")}</SendAllBadge>
                ) : (
                )} */}
              <>
                {/* {selectedRows.map((user: any) => ( */}
                {individualInfor.address?.length > 0 ? (
                  individualInfor.address.map((user: any) => (
                    <div key={user.id}>
                      <p>{user.full_name}</p>
                      <CloseIcon
                        fill="#3B3B3B"
                        onClick={() => {
                          setIndividualInfor({
                            ...individualInfor,
                            address: individualInfor.address.filter(
                              (row: any) => row.id !== user.id
                            ),
                          });
                        }}
                      />
                    </div>
                  ))
                ) : (
                  <></>
                )}
              </>
            </BadgeContainer>
          </TagsContainer>
        </SectionContainer>
        <Controller
          control={control}
          name="message_title"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Input
              value={value}
              label={
                <>
                  {t("subject")}
                  <span style={{ color: "#EB5757", marginLeft: 5 }}>*</span>
                </>
              }
              height={54}
              fw={700}
              fsLabel={16}
              fwLabel={700}
              marginForm="0 0 16px"
              lhLabel="22px"
              bdr={12}
              onChange={onChange}
              error={t(error?.message)}
            />
          )}
        />
        <Controller
          control={control}
          name="message"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextArea
              value={value}
              height="248px"
              fsLabel={16}
              fwLabel={700}
              lhLabel="22px"
              marginForm="0 0 16px"
              onChange={onChange}
              error={t(error?.message)}
              label={
                <>
                  {t("message")}
                  <span style={{ color: "#EB5757", marginLeft: 5 }}>*</span>
                </>
              }
            />
          )}
        />
      </form>

      <div>
        <Label>{t("attachment")}</Label>
        <ImageUploader
          onUploadFileChange={onUploadFileChange}
          fileList={fileList}
          setFileList={setFileList}
        />
      </div>
    </>
  );

  const renderSendMessageModal = () => (
    <SendMessageModalStyled
      type="form"
      title={t("confirm-change-delivery-reservation")}
      visible={isVisible}
      onCancel={() => {
        handleCancelSendMessageModal();
      }}
    >
      {showConfirm && <ConfirmModal />}
      {isShowDeleteModal && <DeleteMessageModal />}

      <form onSubmit={handleSubmit(() => setShowConfirm(true))}>
        <div>
          <WrapperDateAndTimeContainer>
            <WrapperDateAndTimeContent>
              <WrapperDateAndTime>
                <div>{t("delivery-date")}</div>
                <Flex>
                  <DatePicker2
                    onChange={(date: any) => {
                      const currentTimeZone =
                        localStorageHelper.getItem("currentTimeZone");
                      const selectedDate = handleGetTimeByTimeZone(
                        format,
                        currentTimeZone,
                        date
                      );
                      const startDateConverted = handleGetTimeByTimeZone(
                        format,
                        currentTimeZone,
                        individualInfor.startDate
                      );
                      const currentDate = handleGetTimeByTimeZone(
                        format,
                        currentTimeZone,
                        new Date()
                      );

                      if (selectedDate === currentDate) {
                        const { timeOptions, hour } = handleTime();
                        if (startDateConverted === selectedDate) {
                          const hour = handleGetHour(individualInfor.startDate);
                          const hourOption =
                            moment(
                              handleGetTimeByTimeZone(
                                "YYYY/MM/DD HH:mm",
                                currentTimeZone,
                                new Date()
                              )
                            ).hour() + 1;

                          setTimeValue(`${hour}:00`);
                          setTimeOptions(handleTimeOptions(hourOption));
                        } else {
                          setTimeOptions(timeOptions);
                          setTimeValue(`${hour}:00`);
                        }
                      } else {
                        setTimeOptions(timeValueDefaults);
                        if (startDateConverted === selectedDate) {
                          const hour = handleGetHour(individualInfor.startDate);

                          setTimeValue(`${hour}:00`);
                        } else {
                          setTimeValue("00:00");
                        }
                      }
                      setStartDate(date);
                    }}
                    selected={startDate}
                    iconPosition="right"
                    minDate={new Date()}
                    placeholderText="YYYY/MM/DD"
                  />
                </Flex>
              </WrapperDateAndTime>
              <WrapperDateAndTime>
                <div>{t("delivery-time")}</div>
                <Select
                  options={timeOptions}
                  onChange={onChangeTime}
                  value={timeValue}
                  width={screens.xs ? "207px" : "105px"}
                />
              </WrapperDateAndTime>
            </WrapperDateAndTimeContent>
            <ButtonStyled
              // onClick={() => setShowConfirm(true)}
              type="submit"
              name="日時を変更してメッセージを保存"
              background={"#2AC769"}
              color="#FFFFFF"
              border="none"
              fontSize={16}
              fontWeight={700}
              padding="10.5px 9px 9.5px"
              disabled={!individualInfor?.address?.length}
            />
          </WrapperDateAndTimeContainer>
          <CollapseStyled2>
            <Panel key={1} header="メッセージを編集する">
              {renderMessageInfo()}
            </Panel>
          </CollapseStyled2>
          <DeleteMessage onClick={() => setIsShowDeleteModal(true)}>
            {t("delete-message")}
          </DeleteMessage>
        </div>
      </form>
    </SendMessageModalStyled>
  );

  return (
    <>
      {isVisibleNotification.status
        ? renderNotificationModal()
        : renderSendMessageModal()}
    </>
  );
};

export default UpdateMessage;
